import { captureError } from '@smg-real-estate/browser-rum';
import cloneDeep from 'lodash.clonedeep';
import { type ActionContext } from 'vuex';

import {
  deleteInsertion,
  getUnifiedInsertionById,
  patchInsertionStatus,
  postInsertionArchive,
  postInsertionDuplication,
  postInsertionReactivate,
  putPublishAddress,
  searchInsertions,
  type SearchResult,
} from '@/api/insertions';
import { DeactivationReason } from '@/components/InsertionCard/ListingActionModals/modals/DeactivationModal/deactivationReason';
import searchControlQueryDataValidation from '@/helpers/searchControlQueryDataValidation';
import searchFilterQueryDataValidation from '@/helpers/searchFilterQueryDataValidation';
import {
  searchFilterDefaults,
  searchPaginationDefaults,
  type SearchControlData,
  type SearchFilterData,
  type SearchPaginationData,
} from '@/models/insertionsParameters';
import { useUserStore } from '@/stores/user/useUserStore';
import { FiltersWithCount, InsertionStatus, type Insertion } from '@/types';

import {
  applyArchiveOptimisticUpdate,
  applyInsertionUpdate,
  ensureInsertionDateTypes,
  registerInsertionProcessingWithGoal,
  resolveUserIdForInsertion,
  runInsertionProcessingPoll,
  type InsertionProcessingGoal,
} from './insertionProcessingHelpers';
import { publishProjectBoostChange } from './projectBoostPublish';

export type { InsertionProcessingGoal };

export const insertionDisplayOrder: InsertionStatus[] = [
  InsertionStatus.PendingApproval,
  InsertionStatus.PendingRepublishApproval,
  InsertionStatus.Approved,
  InsertionStatus.Draft,
  InsertionStatus.Published,
  InsertionStatus.Republishing,
  InsertionStatus.Deleted,
];

export enum ApiCallStatus {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface InsertionsState {
  insertions: Insertion[];
  apiCallStatus: ApiCallStatus;
  apiCallInsertionId?: string;
  insertionActionSuccessTitle?: string;
  insertionActionSuccessMessage?: string;
  archivedInsertionId?: string;
  deletedInsertionId?: string;
  search: {
    nextPageToFetch: string | undefined;
    nextInsertionStatusToFetch: InsertionStatus;
    insertionStatusesToFetch: InsertionStatus[];
    objectReference: string | undefined;
    username: string | undefined;
    postalCode: string | undefined;
  };
  insertionsChanged?: boolean;
  searchFilter: SearchFilterData;
  searchFilterLatest?: SearchFilterData;
  searchControl: SearchControlData;
  searchPagination: SearchPaginationData;
  filtersWithCount: FiltersWithCount;
  processingInsertionIds: string[];
  processingInsertionGoals: Record<string, InsertionProcessingGoal>;
}

export function findInsertionById(insertions: Insertion[], id: string): Insertion {
  return insertions.find((insertion) => insertion.id === id) as Insertion;
}

function findInsertionIndexById(insertions: Insertion[], id: string): number {
  return insertions.findIndex((insertion) => insertion.id === id);
}

const doUpdateInsertion = (state: InsertionsState, updatedInsertion: Insertion) => {
  const id = updatedInsertion.id;
  const insertionIndex = findInsertionIndexById(state.insertions, id);
  if (insertionIndex >= 0) {
    state.insertions[insertionIndex] = cloneDeep(updatedInsertion);
  }
};

function getInitialState(
  searchFilters?: SearchFilterData,
  searchControl?: SearchControlData,
): InsertionsState {
  return {
    insertions: [],
    apiCallStatus: ApiCallStatus.READY,
    apiCallInsertionId: undefined,
    insertionActionSuccessTitle: undefined,
    insertionActionSuccessMessage: undefined,
    archivedInsertionId: undefined,
    deletedInsertionId: undefined,
    search: {
      nextPageToFetch: undefined,
      nextInsertionStatusToFetch: InsertionStatus.PendingApproval,
      insertionStatusesToFetch: insertionDisplayOrder,
      objectReference: undefined,
      username: undefined,
      postalCode: undefined,
    },
    insertionsChanged: false,
    searchFilter: { ...searchFilterQueryDataValidation(searchFilters || searchFilterDefaults) },
    searchFilterLatest: {
      ...searchFilterQueryDataValidation(searchFilters || searchFilterDefaults),
    },
    searchControl: {
      ...searchControlQueryDataValidation(searchControl),
    },
    searchPagination: { ...searchPaginationDefaults },
    filtersWithCount: { total: 0, filters: [] },
    processingInsertionIds: [],
    processingInsertionGoals: {},
  };
}

const getters = {
  getInsertions: (state: InsertionsState): Insertion[] => state.insertions,
  getInsertionActionSuccessTitle: (state: InsertionsState): string | undefined =>
    state.insertionActionSuccessTitle,
  getInsertionActionSuccessMessage: (state: InsertionsState): string | undefined =>
    state.insertionActionSuccessMessage,
  isApiCallInProgressById: (state: InsertionsState): boolean => !!state.apiCallInsertionId,
  apiCallInProgressById:
    (state: InsertionsState) =>
    (insertionId: string): boolean =>
      !!state.apiCallInsertionId && state.apiCallInsertionId === insertionId,
  apiCallInProgress: (state: InsertionsState): boolean =>
    state.apiCallStatus === ApiCallStatus.IN_PROGRESS,
  apiCallFailed: (state: InsertionsState): boolean => state.apiCallStatus === ApiCallStatus.ERROR,
  apiCallSucceeded: (state: InsertionsState): boolean =>
    state.apiCallStatus === ApiCallStatus.SUCCESS,
  getDeletedInsertionId: (state: InsertionsState): string | undefined => state.deletedInsertionId,
  getInsertionById(state: InsertionsState) {
    return (id: string): Insertion | undefined => findInsertionById(state.insertions, id);
  },
  getListingIdForInsertionId:
    (state: InsertionsState) =>
    (id: string): string | null | undefined =>
      findInsertionById(state.insertions, id)?.listing.id,
  hasNextPage: (state: InsertionsState): boolean =>
    Boolean(state.search.nextPageToFetch || state.search.nextInsertionStatusToFetch),
  hasInsertionsChanged: (state: InsertionsState): boolean | undefined => state.insertionsChanged,
  getSearchFilter: (state: InsertionsState): SearchFilterData => state.searchFilter,
  getSearchFilterLatest: (state: InsertionsState): SearchFilterData | undefined =>
    state.searchFilterLatest,
  getSearchControl: (state: InsertionsState): SearchControlData => state.searchControl,
  getSearchPagination: (state: InsertionsState): SearchPaginationData => state.searchPagination,
  getFiltersWithCount: (state: InsertionsState): FiltersWithCount => state.filtersWithCount,
  getInsertionIdsWithProcessingPublish: (state: InsertionsState): string[] =>
    state.processingInsertionIds.filter((id) => state.processingInsertionGoals[id] === 'publish'),
  getInsertionIdsWithProcessingArchive: (state: InsertionsState): string[] =>
    state.processingInsertionIds.filter((id) => state.processingInsertionGoals[id] === 'archive'),
  getInsertionIdsWithProcessingReactivate: (state: InsertionsState): string[] =>
    state.processingInsertionIds.filter(
      (id) => state.processingInsertionGoals[id] === 'reactivate',
    ),
};

const mutations = {
  setApiCallStatus(state: InsertionsState, status: ApiCallStatus): void {
    state.apiCallStatus = status;
  },
  updateApiCallById(state: InsertionsState, insertionId: string | undefined): void {
    state.apiCallInsertionId = insertionId;
  },
  setInsertionsAsChanged(state: InsertionsState, value: boolean): void {
    state.insertionsChanged = value;
  },
  setSearchControl(state: InsertionsState, payload: SearchControlData): void {
    state.searchControl = { ...state.searchControl, ...payload };
  },
  setSearchPagination(state: InsertionsState, payload: SearchPaginationData): void {
    state.searchPagination = { ...state.searchPagination, ...payload };
  },
  setSearchFilter(state: InsertionsState, payload: SearchFilterData): void {
    state.searchFilter = { ...state.searchFilter, ...payload };
  },
  resetSearchFilter(state: InsertionsState): void {
    state.searchFilter = { ...searchFilterDefaults };
    state.searchFilterLatest = { ...searchFilterDefaults };
  },
  setSearchFilterLatest(state: InsertionsState): void {
    state.searchFilterLatest = { ...state.searchFilter };
  },
  setFiltersWithCount(state: InsertionsState, filtersWithCount: FiltersWithCount): void {
    state.filtersWithCount = filtersWithCount;
  },
  removeAllInsertions(state: InsertionsState): void {
    state.insertions = [];
  },
  addInsertions(state: InsertionsState, payload: SearchResult): void {
    payload.insertions.forEach(ensureProperTypes);
    state.insertions = [...state.insertions, ...payload.insertions];
    state.search.nextPageToFetch = payload.nextPageCursor;
    state.search.nextInsertionStatusToFetch = payload.nextStatus!;
    state.insertionsChanged = false;
  },
  addInsertion(state: InsertionsState, insertion: Insertion): void {
    ensureProperTypes(insertion);
    state.insertions = [insertion, ...state.insertions];
    state.insertionsChanged = true;
  },
  updateInsertion(state: InsertionsState, updatedInsertion: Insertion): void {
    doUpdateInsertion(state, updatedInsertion);
  },
  upsertInsertion(state: InsertionsState, insertion: Insertion): void {
    const insertionIndex = findInsertionIndexById(state.insertions, insertion.id);
    if (insertionIndex >= 0) {
      doUpdateInsertion(state, insertion);
      return;
    }

    ensureProperTypes(insertion);
    state.insertions = [insertion, ...state.insertions];
  },
  registerInsertionProcessing(state: InsertionsState, insertionId: string): void {
    if (state.processingInsertionIds.includes(insertionId)) {
      return;
    }

    state.processingInsertionIds = [...state.processingInsertionIds, insertionId];
  },
  setInsertionProcessingGoal(
    state: InsertionsState,
    payload: { insertionId: string; goal: InsertionProcessingGoal },
  ): void {
    state.processingInsertionGoals = {
      ...state.processingInsertionGoals,
      [payload.insertionId]: payload.goal,
    };
  },
  finishInsertionProcessing(state: InsertionsState, insertionId: string): void {
    state.processingInsertionIds = state.processingInsertionIds.filter((id) => id !== insertionId);
    const { [insertionId]: _removedGoal, ...remainingGoals } = state.processingInsertionGoals;
    state.processingInsertionGoals = remainingGoals;
  },
  removeInsertion(state: InsertionsState, insertionId: string): void {
    const insertionIndex: number = findInsertionIndexById(state.insertions, insertionId);
    if (insertionIndex >= 0) {
      state.insertions.splice(insertionIndex, 1);
      state.insertionsChanged = true;
    }
  },
  updateCurrentInsertionStatus(
    state: InsertionsState,
    payload: { insertionId: string; status: InsertionStatus },
  ): void {
    const { insertionId, status } = payload;
    const insertionIndex = findInsertionIndexById(state.insertions, insertionId);
    const insertion = findInsertionById(state.insertions, insertionId);
    if (insertion) {
      insertion.status = status;
      state.insertions[insertionIndex] = insertion;
    }
  },
  resetState(state: InsertionsState): void {
    Object.assign(state, getInitialState());
  },
  resetStateAndSearchFilters(
    state: InsertionsState,
    payload: { searchFilter?: SearchFilterData; searchControl?: SearchControlData },
  ): void {
    const processingInsertionIds = state.processingInsertionIds;
    const processingInsertionGoals = state.processingInsertionGoals;
    Object.assign(state, getInitialState(payload.searchFilter, payload.searchControl));
    state.processingInsertionIds = processingInsertionIds;
    state.processingInsertionGoals = processingInsertionGoals;
  },
  setListingActionSuccessTitle(state: InsertionsState, title: string): void {
    state.insertionActionSuccessTitle = title;
  },
  setListingActionSuccess(state: InsertionsState, message: string): void {
    state.insertionActionSuccessMessage = message;
  },
  resetListingActionStatus(state: InsertionsState): void {
    state.insertionActionSuccessTitle = undefined;
    state.insertionActionSuccessMessage = undefined;
  },
};

const actions = {
  async fetchFilteredInsertions(context: ActionContext<InsertionsState, any>): Promise<void> {
    context.commit('removeAllInsertions');

    // Combine the values sent to the API
    const requestData = {
      ...context.state.searchFilter,
      ...context.state.searchControl,
    };

    // Updating of the search result page before perform the request.
    context.commit('setSearchPagination', {
      page: requestData.page,
    });

    const response = await searchInsertions(requestData, useUserStore().sourcePlatform);
    context.commit('addInsertions', {
      insertions: response.data,
      nextPageCursor: undefined,
    });

    // Updating of the search result data based on response.
    context.commit('setSearchPagination', {
      pageSize: response.cursors?.pageSize,
      totalPages: response.cursors?.totalPages,
      totalItems: response.cursors?.totalCount,
    });

    context.commit('setFiltersWithCount', response.filtersWithCount);
  },
  async changeInsertionShouldHideAddress(
    context: ActionContext<InsertionsState, any>,
    payload: { insertion: Insertion; shouldHideAddress: boolean },
  ): Promise<void> {
    const { insertion, shouldHideAddress } = payload;
    const sourcePlatform = useUserStore().sourcePlatform;

    try {
      context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);
      await putPublishAddress(insertion.userId, insertion.id, sourcePlatform, shouldHideAddress);
      context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);
    } catch (_) {
      context.commit('setApiCallStatus', ApiCallStatus.ERROR);
    }
  },
  async reactivateInsertion(
    context: ActionContext<InsertionsState, any>,
    insertion: Insertion,
  ): Promise<void> {
    try {
      const sourcePlatform = useUserStore().sourcePlatform;

      context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);

      const reactivatedInsertion = await postInsertionReactivate(
        insertion.userId,
        insertion.id,
        sourcePlatform,
      );

      ensureInsertionDateTypes(reactivatedInsertion);
      const mergedReactivatedInsertion: Insertion = {
        ...reactivatedInsertion,
        userId: reactivatedInsertion.userId ?? insertion.userId,
      };

      if (mergedReactivatedInsertion.id !== insertion.id) {
        context.commit('removeInsertion', insertion.id);
      }
      context.commit('upsertInsertion', mergedReactivatedInsertion);
      context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);
      actions.registerInsertionAfterReactivate(context, mergedReactivatedInsertion);
    } catch (error) {
      context.commit('setApiCallStatus', ApiCallStatus.ERROR);
      captureError(error);
    }
  },
  async archiveInsertion(
    context: ActionContext<InsertionsState, any>,
    {
      insertion,
      archivalReasonKey,
      archivalReasonText,
      isAgencyUser,
    }: {
      insertion: Insertion;
      archivalReasonKey: DeactivationReason;
      archivalReasonText: string;
      isAgencyUser?: boolean;
    },
  ): Promise<void> {
    const sourcePlatform = useUserStore().sourcePlatform;

    context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);
    try {
      await postInsertionArchive(
        insertion.userId,
        insertion.id,
        archivalReasonKey,
        archivalReasonText,
        sourcePlatform,
      );
      applyArchiveOptimisticUpdate(context, insertion, isAgencyUser);
      context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);
      actions.registerInsertionAfterArchive(context, insertion);
    } catch (_) {
      context.commit('setApiCallStatus', ApiCallStatus.ERROR);
    }
  },
  async archiveDraftInsertion(
    context: ActionContext<InsertionsState, any>,
    {
      insertion,
      isAgencyUser,
    }: {
      insertion: Insertion;
      isAgencyUser?: boolean;
    },
  ): Promise<void> {
    const sourcePlatform = useUserStore().sourcePlatform;

    context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);
    try {
      await patchInsertionStatus(
        insertion.userId,
        insertion.id,
        InsertionStatus.Archived,
        sourcePlatform,
      );
      applyArchiveOptimisticUpdate(context, insertion, isAgencyUser);
      context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);
      actions.registerInsertionAfterArchive(context, insertion);
    } catch (error) {
      context.commit('setApiCallStatus', ApiCallStatus.ERROR);
      captureError(error);
    }
  },
  async deleteInsertion(
    context: ActionContext<InsertionsState, any>,
    insertion: Insertion,
  ): Promise<void> {
    const sourcePlatform = useUserStore().sourcePlatform;

    context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);
    try {
      await deleteInsertion(insertion.userId, insertion.id, sourcePlatform);

      context.commit(
        'setListingActionSuccess',
        'manageAdsPage.toasts.insertionSuccessfullyDeleted',
      );
      context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);

      // refetch insertions to have correct listing count
      await actions.fetchFilteredInsertions(context);
    } catch (_) {
      context.commit('setApiCallStatus', ApiCallStatus.ERROR);
    }
  },
  async duplicateInsertion(
    context: ActionContext<InsertionsState, any>,
    payload: { ownerId: string; id: string },
  ): Promise<string> {
    const sourcePlatform = useUserStore().sourcePlatform;
    const duplicatedInsertion = await postInsertionDuplication(
      payload.ownerId,
      payload.id,
      sourcePlatform,
    );
    context.commit('setInsertionsAsChanged', true);
    return duplicatedInsertion.id;
  },
  async refetchInsertionById(
    context: ActionContext<InsertionsState, any>,
    payload: { userId: string; insertionId: string },
  ): Promise<Insertion> {
    const { userId, insertionId } = payload;
    const sourcePlatform = useUserStore().sourcePlatform;
    const insertion = await getUnifiedInsertionById(userId, insertionId, sourcePlatform);

    ensureInsertionDateTypes(insertion);
    applyInsertionUpdate(context, insertion);

    return insertion;
  },
  async pollInsertionProcessingStatus(
    context: ActionContext<InsertionsState, any>,
    insertionId: string,
  ): Promise<void> {
    if (!context.state.processingInsertionIds.includes(insertionId)) {
      return;
    }

    const userId = resolveUserIdForInsertion(context, insertionId);
    const finishProcessing = (): void => {
      context.commit('finishInsertionProcessing', insertionId);
    };

    if (!userId) {
      finishProcessing();
      return;
    }

    await runInsertionProcessingPoll(
      context,
      {
        insertionId,
        userId,
        goal: context.state.processingInsertionGoals[insertionId] ?? 'publish',
        sourcePlatform: useUserStore().sourcePlatform,
      },
      finishProcessing,
    );
  },
  registerInsertionAfterArchive(
    context: ActionContext<InsertionsState, any>,
    insertion: Insertion,
  ): void {
    if (insertion.status === InsertionStatus.Archived) {
      return;
    }

    registerInsertionProcessingWithGoal(context, insertion.id, 'archive');
  },
  registerInsertionAfterReactivate(
    context: ActionContext<InsertionsState, any>,
    insertion: Insertion,
  ): void {
    if (insertion.status === InsertionStatus.Published) {
      return;
    }

    registerInsertionProcessingWithGoal(context, insertion.id, 'reactivate');
  },
  async setProjectBoostAndPublish(
    context: ActionContext<InsertionsState, any>,
    payload: { insertion: Insertion; enabled: boolean },
  ): Promise<boolean> {
    const publishedInsertion = await publishProjectBoostChange(context, payload);

    if (!publishedInsertion) {
      return false;
    }

    actions.registerInsertionAfterPublish(context, publishedInsertion);

    return true;
  },
  registerInsertionAfterPublish(
    context: ActionContext<InsertionsState, any>,
    insertion: Insertion,
  ): void {
    context.commit('upsertInsertion', insertion);

    if (insertion.status !== InsertionStatus.Published) {
      registerInsertionProcessingWithGoal(context, insertion.id, 'publish');
    }

    const funnelListing =
      context.rootGetters.getInsertionId === insertion.id
        ? context.rootState.insertion?.listing
        : undefined;

    context.dispatch(
      'trackToplistingFromFunnelListing',
      {
        insertionId: insertion.id,
        listing: funnelListing ?? insertion.listing,
      },
      { root: true },
    );
  },
};

export function getMessageForStatus(status: InsertionStatus): string | undefined {
  if (status === InsertionStatus.Approved) {
    return 'manageAdsPage.toasts.insertionSuccessfullyApproved';
  }
  if (status === InsertionStatus.Archived) {
    return 'manageAdsPage.toasts.insertionSuccessfullyArchived';
  }
  return undefined;
}

const ensureProperTypes = ensureInsertionDateTypes;

const _default = { state: getInitialState, getters, mutations, actions };
export default _default;
